import { useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Tag,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdAdd, MdCalendarToday, MdDelete } from 'react-icons/md';
import CustomModal from 'components/modal/Modal';
import { useCreatePlanWithSchedule } from 'hooks/usePlans';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import {
  ClinicalEpisode,
  CreatePlanWithScheduleDto,
  Patient,
  WeeklyDayConfig,
  WeeklyScheduleConfig,
} from 'types/models';

// ─── Day helpers ──────────────────────────────────────────────────────────────

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
type DayKey = typeof DAY_KEYS[number];
const DAY_LABELS: Record<DayKey, string> = {
  monday: 'Lun', tuesday: 'Mar', wednesday: 'Mié',
  thursday: 'Jue', friday: 'Vie', saturday: 'Sáb', sunday: 'Dom',
};

function emptyDay(): WeeklyDayConfig {
  return { disponible: false, franjas: [] };
}

function defaultSchedule(): WeeklyScheduleConfig {
  return {
    repeticion: 'semanal',
    zonaHoraria: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Guayaquil',
    monday:    { disponible: true,  franjas: [{ inicio: '08:00', fin: '18:00' }] },
    tuesday:   { disponible: true,  franjas: [{ inicio: '08:00', fin: '18:00' }] },
    wednesday: { disponible: true,  franjas: [{ inicio: '08:00', fin: '18:00' }] },
    thursday:  { disponible: true,  franjas: [{ inicio: '08:00', fin: '18:00' }] },
    friday:    { disponible: true,  franjas: [{ inicio: '08:00', fin: '18:00' }] },
    saturday:  emptyDay(),
    sunday:    emptyDay(),
  };
}

// ─── Day row component ────────────────────────────────────────────────────────

interface DayRowProps {
  day: DayKey;
  config: WeeklyDayConfig;
  onChange: (cfg: WeeklyDayConfig) => void;
}

function DayRow({ day, config, onChange }: DayRowProps) {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const addFranja = () => {
    onChange({ ...config, franjas: [...config.franjas, { inicio: '09:00', fin: '17:00' }] });
  };

  const removeFranja = (i: number) => {
    onChange({ ...config, franjas: config.franjas.filter((_, idx) => idx !== i) });
  };

  const updateFranja = (i: number, field: 'inicio' | 'fin', val: string) => {
    const updated = config.franjas.map((f, idx) => idx === i ? { ...f, [field]: val } : f);
    onChange({ ...config, franjas: updated });
  };

  return (
    <Box
      borderRadius='8px'
      border='1px solid'
      borderColor={borderColor}
      p='10px 12px'
      opacity={config.disponible ? 1 : 0.55}
    >
      <Flex align='center' gap='10px' mb={config.disponible && config.franjas.length > 0 ? '8px' : '0'}>
        <Checkbox
          isChecked={config.disponible}
          onChange={e => onChange({ ...config, disponible: e.target.checked, franjas: e.target.checked && config.franjas.length === 0 ? [{ inicio: '08:00', fin: '18:00' }] : config.franjas })}
          colorScheme='teal'
          size='sm'
        />
        <Text fontSize='sm' fontWeight='600' w='36px' flexShrink={0}>{DAY_LABELS[day]}</Text>
        {!config.disponible && (
          <Text fontSize='xs' color={mutedColor}>No disponible</Text>
        )}
        {config.disponible && (
          <IconButton
            aria-label='Agregar franja'
            icon={<Icon as={MdAdd} />}
            size='xs'
            variant='ghost'
            colorScheme='teal'
            ml='auto'
            onClick={addFranja}
          />
        )}
      </Flex>
      {config.disponible && config.franjas.map((f, i) => (
        <Flex key={i} align='center' gap='6px' mt='4px'>
          <Input
            size='xs'
            type='time'
            value={f.inicio}
            onChange={e => updateFranja(i, 'inicio', e.target.value)}
            w='90px'
            borderRadius='6px'
          />
          <Text fontSize='xs' color={mutedColor}>–</Text>
          <Input
            size='xs'
            type='time'
            value={f.fin}
            onChange={e => updateFranja(i, 'fin', e.target.value)}
            w='90px'
            borderRadius='6px'
          />
          <IconButton
            aria-label='Eliminar franja'
            icon={<Icon as={MdDelete} />}
            size='xs'
            variant='ghost'
            colorScheme='red'
            onClick={() => removeFranja(i)}
          />
        </Flex>
      ))}
    </Box>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  patient: Patient;
  episode: ClinicalEpisode;
}

export default function PlanWithScheduleFormModal({ isOpen, onClose, patient, episode }: Props) {
  const toast = useToast();
  const { data: me } = useCurrentDbUser();
  const createMut = useCreatePlanWithSchedule();

  const [titulo, setTitulo] = useState('');
  const [numeroSesiones, setNumeroSesiones] = useState(8);
  const [duracionMinutos, setDuracionMinutos] = useState(60);
  const [startDate, setStartDate] = useState('');
  const [maxPorDia, setMaxPorDia] = useState(1);
  const [observaciones, setObservaciones] = useState('');
  const [schedule, setSchedule] = useState<WeeklyScheduleConfig>(defaultSchedule());

  const bgSection = useColorModeValue('gray.50', 'whiteAlpha.50');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const activeDays = DAY_KEYS.filter(d => schedule[d].disponible);
  const canSubmit = titulo.trim().length >= 3 && startDate && activeDays.length > 0 && !!me;

  const handleDayChange = (day: DayKey, cfg: WeeklyDayConfig) => {
    setSchedule(prev => ({ ...prev, [day]: cfg }));
  };

  const handleClose = () => {
    setTitulo('');
    setNumeroSesiones(8);
    setDuracionMinutos(60);
    setStartDate('');
    setMaxPorDia(1);
    setObservaciones('');
    setSchedule(defaultSchedule());
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || !me) return;

    const dto: CreatePlanWithScheduleDto = {
      titulo: titulo.trim(),
      numeroSesiones,
      duracionMinutos,
      professionalId: me.id,
      startDate,
      maxSesionesPorDia: maxPorDia,
      weeklySchedule: schedule,
      observaciones: observaciones.trim() || undefined,
    };

    try {
      const result = await createMut.mutateAsync({
        patientId: patient.id,
        episodeId: episode.id,
        dto,
      });
      toast({
        title: 'Plan creado',
        description: `${result.sessions.length} sesiones y citas agendadas`,
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      handleClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'No se pudo crear el plan';
      toast({
        title: 'Error al crear plan',
        description: msg,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const modalTitle = (
    <Flex align='center' gap='12px'>
      <Flex w='36px' h='36px' bg='teal.500' borderRadius='10px' align='center' justify='center' flexShrink={0}>
        <Icon as={MdCalendarToday} color='white' w='18px' h='18px' />
      </Flex>
      <Flex direction='column'>
        <Text fontSize='md' fontWeight='800'>Nuevo Plan con Agenda</Text>
        <Text color='secondaryGray.600' fontSize='sm' fontWeight='400'>
          {patient.nombres} {patient.apellidos}
        </Text>
      </Flex>
    </Flex>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      size='lg'
      scrollBehavior='inside'
      title={modalTitle}
      primaryBtnLabel='Crear Plan y Agendar'
      secondaryBtnLabel='Cancelar'
      onPrimaryClick={handleSubmit}
      primaryBtnLoading={createMut.isPending}
      primaryBtnDisabled={!canSubmit}
    >
      <VStack spacing='16px' align='stretch'>

        {/* Basic info */}
        <Box bg={bgSection} borderRadius='10px' p='14px'>
          <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' letterSpacing='wider' mb='12px'>
            Información del Plan
          </Text>
          <VStack spacing='10px' align='stretch'>
            <FormControl isRequired>
              <FormLabel fontSize='xs' mb='4px'>Título del plan</FormLabel>
              <Input
                size='sm'
                placeholder='Rehabilitación esguince hombro…'
                value={titulo}
                onChange={e => setTitulo(e.target.value)}
                borderRadius='8px'
                maxLength={200}
              />
            </FormControl>

            <HStack spacing='10px'>
              <FormControl isRequired>
                <FormLabel fontSize='xs' mb='4px'>Sesiones</FormLabel>
                <NumberInput size='sm' min={1} max={60} value={numeroSesiones} onChange={v => setNumeroSesiones(Number(v))} borderRadius='8px'>
                  <NumberInputField borderRadius='8px' />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='xs' mb='4px'>Duración (min)</FormLabel>
                <NumberInput size='sm' min={15} max={180} step={5} value={duracionMinutos} onChange={v => setDuracionMinutos(Number(v))} borderRadius='8px'>
                  <NumberInputField borderRadius='8px' />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='xs' mb='4px'>Fecha inicio</FormLabel>
                <Input
                  size='sm'
                  type='date'
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  borderRadius='8px'
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel fontSize='xs' mb='4px'>Máx. sesiones/día</FormLabel>
              <NumberInput size='sm' min={1} max={8} value={maxPorDia} onChange={v => setMaxPorDia(Number(v))} w='80px' borderRadius='8px'>
                <NumberInputField borderRadius='8px' />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </VStack>
        </Box>

        {/* Weekly schedule */}
        <Box bg={bgSection} borderRadius='10px' p='14px'>
          <Flex align='center' justify='space-between' mb='12px'>
            <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' letterSpacing='wider'>
              Disponibilidad Semanal
            </Text>
            {activeDays.length > 0 && (
              <Flex gap='4px' flexWrap='wrap' justify='flex-end'>
                {activeDays.map(d => (
                  <Tag key={d} size='sm' colorScheme='teal' borderRadius='full' fontSize='9px'>
                    {DAY_LABELS[d]}
                  </Tag>
                ))}
              </Flex>
            )}
          </Flex>
          <VStack spacing='6px' align='stretch'>
            {DAY_KEYS.map(day => (
              <DayRow
                key={day}
                day={day}
                config={schedule[day]}
                onChange={cfg => handleDayChange(day, cfg)}
              />
            ))}
          </VStack>
          <FormHelperText mt='8px' fontSize='xs'>
            El sistema asignará las sesiones a los días disponibles en orden.
          </FormHelperText>
        </Box>

        {/* Observaciones */}
        <FormControl>
          <FormLabel fontSize='xs' mb='4px'>Observaciones</FormLabel>
          <Textarea
            size='sm'
            rows={2}
            placeholder='Notas del plan…'
            value={observaciones}
            onChange={e => setObservaciones(e.target.value)}
            borderRadius='8px'
            resize='none'
            maxLength={1000}
          />
        </FormControl>

      </VStack>
    </CustomModal>
  );
}
